<template>
  <div
    :id="`${id}_wrap`"
    class="timepicker"
    v-click-outside="closePicker">
    <input
      v-if="formInputName"
      type="hidden"
      :name="formInputName"
      :value="internalValue" />
    <input
      ref="input"
      autocomplete="off"
      :aria-labelledby="`${id}_label`"
      class="timepicker__input"
      v-model="internalValue"
      @click="open = true"
      @change="formatValue"
      @keydown="keydownhandler" />
    <button
      type="button"
      :aria-labelledby="`${id}_label`"
      aria-haspopup="listbox"
      :class="{
        'timepicker__dropdown-button': true,
        'timepicker__dropdown-button--state-disabled': disabled,
        'timepicker__dropdown-button--state-error': error
      }"
      @click="togglePickerOpen">
      <svgicon
        original
        class="combobox__icon"
        data="@iconPath/glyph/16/small-triangle-down.svg?inline"
        width="16"
        height="16"></svgicon>
    </button>
    <ul
      role="listbox"
      :class="{
        'dropdown': true,
        'timepicker__dropdown': true,
        'timepicker__dropdown--state-open': open
      }"
      tab-index="-1"
      :aria-expanded="open"
      @keydown="keydownhandler">
      <li
        v-for="(timeRow, index) in timeRows"
        :key="timeRow.time"
        :ref="'row-' + index"
        :class="{
          'timepicker__listitem': true,
          'timepicker__listitem--selected': index === selectedRow,
          'timepicker__listitem--current': timeRow.text === internalValue
        }"
        @click="selectValue(timeRow)"
        role="option"
        :aria-selected="timeRow.selected"
        tabindex="0">
        {{ timeRow.text }}
      </li>
    </ul>
  </div>
</template>

<script>
import ClickOutside from '@/directives/clickOutside'

export default {
  name: 'TimePickerComponent',
  directives: {
    ClickOutside
  },
  emits: ['change'],
  props: {
    id: String,
    modelValue: String,
    name: String,
    minTime: {
      type: String,
      default: '12:00am'
    },
    maxTime: String,
    step: {
      type: Number,
      default: 15
    },
    disableTimeRanges: {
      type: Array,
      default: function () {
        return []
      }
    },
    timeFormat: {
      type: String,
      default: 'h:i a'
    },
    error: Boolean,
    disabled: Boolean,
    // This is used if this component is used in a form outside of the legacy code
    formInputName: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      internalValue: '12:00 am',
      open: false,
      selectedRow: -1,
      timeRows: []
    }
  },
  created: function () {
    this._baseDate = new Date(1970, 1, 1, 0, 0, 0)
    this._ONE_DAY = 86400
    this._lang = {
      am: 'am',
      pm: 'pm',
      AM: 'AM',
      PM: 'PM',
      decimal: '.',
      mins: 'mins',
      hr: 'hr',
      hrs: 'hrs'
    }

    this.internalValue = this.modelValue
    this.formatValue()

    var start = this.minTime !== undefined ? this._time2int(this.minTime) : 0
    var end =
      this.maxTime !== undefined
        ? this._time2int(this.maxTime)
        : start + this._ONE_DAY - 1

    if (end < start) {
      // make sure the end time is greater than start time, otherwise there will be no list to show
      end += this._ONE_DAY
    }

    var dr = this.disableTimeRanges
    var drCur = 0
    var drLen = dr.length

    // Make sure interval is not 0, causes infinte loop in for below
    var minuteInterval = this.step !== 0 ? this.step : 15

    for (var i = start; i <= end; i += minuteInterval * 60) {
      var timeInt = i
      var timeString = this._int2time(timeInt)
      var row = {}
      row.time = timeInt <= 86400 ? timeInt : timeInt % 86400
      row.text = timeString
      row.selected = row.text === this.internalValue

      if (drCur < drLen) {
        if (timeInt >= dr[drCur][1]) {
          drCur += 1
        }

        if (dr[drCur] && timeInt >= dr[drCur][0] && timeInt < dr[drCur][1]) {
          row.disabled = true
        }
      }

      this.timeRows.push(row)
    }
  },
  computed: {},
  methods: {
    formatValue() {
      var seconds = this._time2int(this.internalValue)

      var rangeError = false
      // check that the time in within bounds
      if (this.minTime !== null && seconds < this.minTime) {
        rangeError = true
      } else if (this.maxTime !== null && seconds > this.maxTime) {
        rangeError = true
      }

      // check that time isn't within disabled time ranges
      for (var i = 0; i < this.disableTimeRanges.length; i++) {
        var timeRange = this.disableTimeRanges[i]
        if (seconds >= timeRange[0] && seconds < timeRange[1]) {
          rangeError = true
          return false
        }
      }

      var prettyTime = this._int2time(seconds)

      if (rangeError) {
        console.log('Range error.')
      } else {
        this.internalValue = prettyTime
        // Emit change up to the parent
        this.$emit('change', this.internalValue)
      }
    },
    togglePickerOpen(e) {
      e.preventDefault()
      if (this.open) {
        this.closePicker()
      } else {
        this.openPicker()
      }
    },
    openPicker() {
      this.open = true
      this.selectRow(0)
    },
    selectRow(index) {
      this.selectedRow = index
      this.$nextTick(() => {
        if (
          this.selectedRow >= 0 &&
          this.selectedRow <= this.timeRows.length - 1
        ) {
          this.$refs['row-' + this.selectedRow][0].focus()
        }
      })
    },
    closePicker() {
      this.open = false
    },
    roundingFunction(seconds, settings) {
      if (seconds === null) {
        return null
      } else {
        var offset = seconds % (settings.step * 60) // step is in minutes

        if (offset >= settings.step * 30) {
          // if offset is larger than a half step, round up
          seconds += settings.step * 60 - offset
        } else {
          // round down
          seconds -= offset
        }

        return seconds
      }
    },
    keydownhandler(e) {
      if (!this.open) {
        if (e.keyCode === 40 || e.keyCode === 13) {
          e.preventDefault()
          this.openPicker()
        }
        return
      }

      switch (e.keyCode) {
        case 96: // numpad numerals
        case 97:
        case 98:
        case 99:
        case 100:
        case 101:
        case 102:
        case 103:
        case 104:
        case 105:
        case 48: // numerals
        case 49:
        case 50:
        case 51:
        case 52:
        case 53:
        case 54:
        case 55:
        case 56:
        case 57:
        case 65: // a
        case 77: // m
        case 80: // p
        case 186: // colon
        case 8: // backspace
        case 46: // delete
          this.closePicker()
          break

        case 13: // return
          e.preventDefault()
          if (
            this.selectedRow >= 0 &&
            this.selectedRow < this.timeRows.length
          ) {
            var timeRow = this.timeRows[this.selectedRow]
            this.selectValue(timeRow)
            this.$refs['input'].focus()
          }
          break

        case 38: // up
          e.preventDefault()
          this.selectRow(this.selectedRow <= 0 ? 0 : this.selectedRow - 1)
          break

        case 40: // down
          e.preventDefault()
          this.selectRow(
            this.selectedRow >= this.timeRows.length - 1
              ? this.timeRows.length - 1
              : this.selectedRow + 1
          )
          break

        case 27: // escape
          this.selectedRow = -1
          this.closePicker()
          this.$refs['input'].focus()
          break

        case 9: //tab
          this.closePicker()
          break

        default:
          return
      }
    },
    selectValue(timeRow) {
      if (!timeRow.disabled) {
        let prevSelection = this.timeRows.find(
          (el) => el.text === this.internalValue
        )
        let currentSelection = this.timeRows.find(
          (el) => el.text === timeRow.text
        )

        if (prevSelection) {
          prevSelection.selected = false
        }

        if (currentSelection) {
          currentSelection.selected = true
        }

        this.internalValue = timeRow.text
        // Emit change up to the parent
        this.$emit('change', this.internalValue)
        this.closePicker()
        return true
      }
      return false
    },
    _int2time(seconds) {
      if (seconds === null) {
        return
      }

      var time = new Date(this._baseDate.valueOf() + seconds * 1000)

      if (isNaN(time.getTime())) {
        return
      }

      var output = ''
      var hour, code
      for (var i = 0; i < this.timeFormat.length; i++) {
        code = this.timeFormat.charAt(i)
        switch (code) {
          case 'a':
            output += time.getHours() > 11 ? this._lang.pm : this._lang.am
            break

          case 'A':
            output += time.getHours() > 11 ? this._lang.PM : this._lang.AM
            break

          case 'g':
            hour = time.getHours() % 12
            output += hour === 0 ? '12' : hour
            break

          case 'G':
            hour = time.getHours()
            if (seconds === this._ONE_DAY) hour = 24
            output += hour
            break

          case 'h':
            hour = time.getHours() % 12

            if (hour !== 0 && hour < 10) {
              hour = '0' + hour
            }

            output += hour === 0 ? '12' : hour
            break

          case 'H':
            hour = time.getHours()
            if (seconds === this._ONE_DAY) hour = 24
            output += hour > 9 ? hour : '0' + hour
            break

          case 'i':
            var minutes = time.getMinutes()
            output += minutes > 9 ? minutes : '0' + minutes
            break

          case 's':
            seconds = time.getSeconds()
            output += seconds > 9 ? seconds : '0' + seconds
            break

          case '\\':
            // escape character; add the next character and skip ahead
            i++
            output += this.timeFormat.charAt(i)
            break

          default:
            output += code
        }
      }

      return output
    },
    _time2int(timeString) {
      if (timeString === '') return null
      if (!timeString || timeString + 0 === timeString) return timeString

      if (typeof timeString === 'object') {
        return (
          timeString.getHours() * 3600 +
          timeString.getMinutes() * 60 +
          timeString.getSeconds()
        )
      }

      timeString = timeString.toLowerCase().replace('.', '')

      // if the last character is an "a" or "p", add the "m"
      if (timeString.slice(-1) === 'a' || timeString.slice(-1) === 'p') {
        timeString += 'm'
      }

      var ampmRegex =
        '(' +
        this._lang.am.replace('.', '') +
        '|' +
        this._lang.pm.replace('.', '') +
        '|' +
        this._lang.AM.replace('.', '') +
        '|' +
        this._lang.PM.replace('.', '') +
        ')?'

      // try to parse time input
      var pattern = new RegExp(
        '^' +
          ampmRegex +
          '\\s*([0-2]?[0-9])\\W?([0-5][0-9])?\\W?([0-5][0-9])?\\s*' +
          ampmRegex +
          '$'
      )

      var time = timeString.match(pattern)
      if (!time) {
        return null
      }

      var hour = parseInt(time[2] * 1, 10)
      var ampm = time[1] || time[5]
      var hours = hour

      if (hour <= 12 && ampm) {
        var isPm = ampm === this._lang.pm || ampm === this._lang.PM

        if (hour === 12) {
          hours = isPm ? 12 : 0
        } else {
          hours = hour + (isPm ? 12 : 0)
        }
      }

      var minutes = time[3] * 1 || 0
      var seconds = time[4] * 1 || 0
      var timeInt = hours * 3600 + minutes * 60 + seconds

      // if no am/pm provided, intelligently guess based on the scrollDefault
      if (!ampm && this._twelveHourTime && this.scrollDefault) {
        var delta = timeInt - this.scrollDefault
        if (delta < 0 && delta >= this._ONE_DAY / -2) {
          timeInt = (timeInt + this._ONE_DAY / 2) % this._ONE_DAY
        }
      }

      return timeInt
    },
    _pad2(n) {
      return ('0' + n).slice(-2)
    }
  }
}
</script>
